<script setup lang="ts">
import validIcon from "@/assets/img/tick.png";
import errorIcon from "@/assets/img/cross.png";
import type { RadioOption } from "@/models";

const props = defineProps<{
    id: string;
    inputName: string;
    options: RadioOption[];
    disabled?: boolean;
    hasError?: boolean;
}>();

const model = defineModel<string | number | boolean | null | undefined>();

const emit = defineEmits<{
    change: [value: string | number | boolean];
}>();

const handleSelect = (value: string | number | boolean) => {
    if (props.disabled) return;
    model.value = value;
    emit("change", value);
};

const showIcon = (optionValue: string | number | boolean) => {
    return model.value === optionValue || props.hasError;
};

const getIcon = (optionValue: string | number | boolean) => {
    if (props.hasError) return errorIcon;
    if (model.value === optionValue) return validIcon;
    return null;
};
</script>

<template>
    <div class="radio-group">
        <div
            v-for="option in options"
            :key="`${props.id}-${String(option.value)}`"
            class="radio-option"
            :class="{
                selected: model === option.value,
                error: hasError,
            }"
            tabindex="0"
            @click="handleSelect(option.value)"
            @keydown.enter="handleSelect(option.value)"
            @keydown.space.prevent="handleSelect(option.value)"
        >
            <span class="label">{{ option.label }}</span>
            <img
                v-if="showIcon(option.value)"
                :src="getIcon(option.value)!"
                class="icon"
                :alt="hasError ? 'error' : 'selected'"
            />
        </div>
    </div>
</template>

<style scoped>
.radio-group {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.radio-option {
    width: 100%;
    height: 46px;
    flex: 1 1 calc(33.333% - 0.75rem);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 15px;
    border: 1px solid var(--grey-border);
    border-radius: 3px;
    background-color: var(--radio-btn-background);
    cursor: pointer;
    font-size: var(--font-size-input);
    box-sizing: border-box;
    position: relative;
    transition: all 0.2s;
}

.radio-option:focus {
    outline: none;
    border-color: var(--grey-dark);
}

.radio-option:hover:not(.error) {
    border-color: var(--grey-dark);
}

.radio-option.selected {
    border-color: var(--color-primary-light) !important;
    background-color: var(--selection-background) !important;
}

.radio-option.error {
    border-color: var(--error-border) !important;
    background-color: var(--error-background) !important;
}

.label {
    user-select: none;
    color: var(--off-black);
    max-width: 90%;
}

.icon {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
}

@media (width <= 600px) {
    .radio-group {
        flex-direction: column;
        gap: 15px;
    }

    .radio-option {
        flex: 1 1 100%;
        width: 100%;
    }
}
</style>
